import React, { useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from '../hooks/useChartDimensions';
import { filterVisibleItems, useLegendToggle } from '../hooks/useLegendToggle';
import ChartLegend from './ChartLegend';

/**
 * Donut chart with a centered headline value.
 * data: [{ label, value, color }]
 */
export default function DonutChart({
  data,
  valueFormat = (v) => v.toLocaleString(),
  centerLabel,
  centerValue,
  heightRatio = 0.7,
}) {
  const { containerRef, svgRef, tooltipRef, dimensions, hideTooltip } = useChartDimensions(
    (w) => Math.min(360, Math.max(220, w * heightRatio))
  );

  const segmentKeys = useMemo(
    () => (data || []).map((d) => d.label),
    [data]
  );
  const segmentLabels = useMemo(
    () => Object.fromEntries((data || []).map((d) => [d.label, d.label])),
    [data]
  );
  const legendItems = useMemo(
    () => (data || []).map((d) => ({
      key: d.label,
      label: d.label,
      color: d.color,
      value: valueFormat(d.value),
    })),
    [data, valueFormat]
  );
  const { isVisible, toggle, statusMessage, hiddenKeys } = useLegendToggle(segmentKeys, {
    labels: segmentLabels,
  });
  const visibleData = useMemo(
    () => filterVisibleItems(data, hiddenKeys, (d) => d.label),
    [data, hiddenKeys]
  );

  useEffect(() => {
    if (!visibleData?.length || !dimensions.width) {
      hideTooltip();
      return;
    }
    const { width, height } = dimensions;
    const size = Math.min(width, height);
    const radius = size / 2 - 10;
    const innerR = radius * 0.62;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${width} ${height}`);
    const g = svg.append('g').attr('transform', `translate(${width / 2}, ${height / 2})`);
    const tooltip = d3.select(tooltipRef.current);

    const pie = d3.pie().value((d) => d.value).sort(null);
    const arc = d3.arc().innerRadius(innerR).outerRadius(radius).cornerRadius(2);

    g.selectAll('.arc').data(pie(visibleData)).enter().append('path')
      .attr('d', arc)
      .attr('fill', (d) => d.data.color || '#94A3B8')
      .attr('stroke', '#FFFFFF')
      .attr('stroke-width', 2)
      .on('mouseenter', (event, d) => {
        tooltip.style('opacity', 1)
          .style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`)
          .html(`<div class="tip-label">${d.data.label}</div><div><strong>${valueFormat(d.data.value)}</strong> (${((d.endAngle - d.startAngle) / (2 * Math.PI) * 100).toFixed(1)}%)</div>`);
      })
      .on('mousemove', (event) => tooltip.style('left', `${event.clientX + 12}px`).style('top', `${event.clientY - 10}px`))
      .on('mouseleave', hideTooltip);

    if (centerValue) {
      g.append('text').attr('text-anchor', 'middle')
        .attr('dy', '-0.1em')
        .attr('font-size', `${Math.max(15, radius * 0.22)}px`)
        .attr('font-weight', '800')
        .attr('fill', '#0A1E3A').text(centerValue);
    }
    if (centerLabel) {
      g.append('text').attr('text-anchor', 'middle')
        .attr('dy', '1.4em')
        .attr('font-size', '11px')
        .attr('font-weight', '700')
        .attr('letter-spacing', '0.06em')
        .attr('text-transform', 'uppercase')
        .attr('fill', '#64748B').text(centerLabel);
    }
    return hideTooltip;
  }, [visibleData, dimensions, valueFormat, centerLabel, centerValue, hideTooltip, svgRef, tooltipRef]);

  if (!data?.length) return <div className="chart-container"><div className="no-data">No data</div></div>;
  return (
    <div className="chart-container" ref={containerRef}>
      <svg ref={svgRef} />
      <div className="d3-tooltip" ref={tooltipRef} style={{ opacity: 0 }} />
      <ChartLegend
        items={legendItems}
        isVisible={isVisible}
        onToggle={toggle}
        statusMessage={statusMessage}
      />
    </div>
  );
}
